/* eslint-disable camelcase */
import { isEmpty, isEqual } from "lodash";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import { usePage } from "PFApp/use_page";
import { fetchPaginatedCollection, fetchPaginatedDocs } from "PFCore/helpers/collection";
import { useDeterministicStringify } from "PFCore/helpers/use_deterministic_stringity";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import i18n from "PFCore/i18n";
import { createDictionaryConnection } from "PFCore/services/custom_values";
import { ApiRoute } from "PFCore/utilities/routes";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { getAPIFilters, getValueFilters, handleRequestFiltersChange } from "../use_filtered_collection";

export const useFeedPage = (initPage) => {
  const { data: currentProfile } = useCurrentProfile();

  const stringify = useDeterministicStringify();

  const history = useHistory();
  const location = useLocation();
  const perPage = 10;

  const commonFilters = {
    fields: {
      state: ["draft", "new"],
      template_key: [
        TEMPLATE_KEYS.EVENT,
        TEMPLATE_KEYS.POSITION,
        TEMPLATE_KEYS.PROJECT,
        TEMPLATE_KEYS.QUESTION,
        TEMPLATE_KEYS.ROLE,
        TEMPLATE_KEYS.SUGGESTION,
        TEMPLATE_KEYS.POST,
        TEMPLATE_KEYS.LESSON,
        TEMPLATE_KEYS.ENGAGEMENT
      ]
    }
  };

  const myFeedFilters = {
    ...commonFilters,
    fields: { ...commonFilters.fields, _invited: "true" }
  };

  const myActivitiesFilters = {
    ...commonFilters,
    fields: {
      ...commonFilters.fields,
      contributor_id: currentProfile.id.toString(),
      owner_id: currentProfile.id.toString()
    }
  };

  const filtersForTab = (tab) =>
    ({
      my_feed: { ...myFeedFilters },
      my_activities: { ...myActivitiesFilters },
      all_activities: { ...commonFilters }
    }[tab]);

  const { page, setPage, mergePage, resetPage } = usePage("feedPage", initPage);

  const [tab, setTab] = useState(initPage.section || "my_feed");
  const [requestFilters, setRequestFilters] = useState(filtersForTab(initPage.section || "my_feed"));
  const [requestOrder, setRequestOrder] = useState();
  const [fetchParams, setFetchParams] = useState({});
  const clearRef = useRef(0);

  const filtersAreClear =
    (tab === "my_feed" && isEqual(getAPIFilters(requestFilters), myFeedFilters)) ||
    (tab === "my_activities" && isEqual(getAPIFilters(requestFilters), myActivitiesFilters)) ||
    isEqual(getAPIFilters(requestFilters), commonFilters);

  const orderParam = requestOrder ? { order: requestOrder } : {};

  const fetchActivities = (page = 1, perPage = perPage) => {
    const _fetchParams = {
      page,
      perPage,
      filters: getAPIFilters(requestFilters),
      ...orderParam
    };
    setFetchParams(_fetchParams);

    return fetchPaginatedCollection(ApiRoute(`/api/activities`), _fetchParams)
      .then((response) => {
        mergePage({
          loaded: true,
          tasks: response,
          clearRefValue: clearRef.current
        });
        setRequestFilters(getValueFilters(response.meta.filters));
        setRequestOrder(response.meta.order.value);
      })
      .catch(() =>
        mergePage({
          loaded: true,
          errors: [i18n.t("thereWasAnError")]
        })
      );
  };

  const fetchActivitiesDocs = (docType) =>
    fetchPaginatedDocs(docType, ApiRoute(`/api/activities`), fetchParams);

  const saveInterestedSkill = (id) =>
    createDictionaryConnection({ experience: null, interest: true, customValueId: id })
      .then(() =>
        mergePage({
          loaded: true
        })
      )
      .catch(() =>
        mergePage({
          loaded: true,
          errors: [i18n.t("thereWasAnError")]
        })
      );

  useEffect(() => {
    setPage(initPage);
    if (tab !== initPage.section && initPage.section) {
      setTab(initPage.section);
    } else if (tab && initPage.section === undefined) {
      setTab("my_feed");
    }
    return resetPage;
  }, []);

  const toggleFilters = () => mergePage({ showFilters: !page.showFilters });
  const clearFilters = useCallback(() => {
    clearRef.current++;
    setRequestFilters(
      {
        my_feed: { ...myFeedFilters },
        my_activities: { ...myActivitiesFilters },
        all_activities: { ...commonFilters }
      }[tab]
    );
  }, [tab]);

  const handleFilterChange = (filter, value) => {
    if (filter.name === "term" && !isEmpty(value)) {
      setRequestOrder({
        direction: "desc",
        name: ["scores.term_score"],
        text: i18n.t("search:searchBox.mostRelevant")
      });
    }

    setRequestFilters((requestFilters) => {
      const newFilters = handleRequestFiltersChange(requestFilters, filter, value);
      return newFilters;
    });
  };

  const handleFiltersReplace = (filters) => {
    setRequestFilters(filters);
  };

  const handleEditPost = (taskId) => {
    $.ajax({
      url: ApiRoute(`/api/activities/${taskId}`),
      api_version: 2
    }).then((resp) => mergePage({ postTask: resp }));
  };

  const fetchCourses = (matchingCourses) => {
    if (matchingCourses) {
      // this is the default option
      return $.ajax({
        url: ApiRoute("/api/courses"),
        api_version: 2
      });
    } else {
      return $.ajax({
        url: ApiRoute("/api/activities"),
        api_version: 2,
        data: { filters: { fields: { template_key: [TEMPLATE_KEYS.COURSE] } } }
      });
    }
  };

  const fetchRssFeed = () =>
    $.ajax({
      url: ApiRoute("/api/feed_items"),
      api_version: 2
    });

  useEffect(() => {
    fetchActivities(1, perPage);
  }, [stringify(getAPIFilters(requestFilters)), stringify(requestOrder)]);

  useEffect(() => {
    mergePage({ showFilters: false });
    const url = `/feed/${tab}`;
    if (location.pathname !== url) {
      history.push(url);
    }
    setRequestFilters(filtersForTab(tab));
  }, [tab]);

  return {
    ...page,
    tab,
    setTab,
    fetchActivities,
    fetchActivitiesDocs,
    saveInterestedSkill,
    toggleFilters,
    clearFilters,
    handleOrderChange: setRequestOrder,
    handleFilterChange,
    handleFiltersReplace,
    handleEditPost,
    fetchCourses,
    fetchRssFeed,
    filtersAreClear
  };
};
